/**
 * ActivateModuleModal.tsx — F7a T7.4.
 *
 * Modal z formularzem seats + periodMonths, wywołujący
 * POST /api/admin/tenants/:id/modules/:code/activate.
 *
 * Reset stanu przy re-open (pattern z CreateTenantModal).
 */

import { type FormEvent, useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { AxiosError } from 'axios';

import { Button } from '@/components/Button';
import { FormField } from '@/components/FormField';
import { Input } from '@/components/Input';
import { Modal } from '@/components/Modal';
import { api } from '@/lib/api';

export interface ActivateModuleModalProps {
  open: boolean;
  tenantId: string;
  moduleCode: string;
  moduleName: string;
  onClose: () => void;
  onSuccess: () => void;
}

type Period = 1 | 12;

export function ActivateModuleModal({
  open,
  tenantId,
  moduleCode,
  moduleName,
  onClose,
  onSuccess,
}: ActivateModuleModalProps) {
  const { t } = useTranslation();
  const formId = useId();
  const resolvedFormId = `activate-module-form-${formId}`;

  const [seats, setSeats] = useState<string>('1');
  const [period, setPeriod] = useState<Period>(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [seatsError, setSeatsError] = useState<string | null>(null);

  const prevOpenRef = useRef(false);
  useEffect(() => {
    if (open && !prevOpenRef.current) {
      setSeats('1');
      setPeriod(1);
      setIsSubmitting(false);
      setApiError(null);
      setSeatsError(null);
    }
    prevOpenRef.current = open;
  }, [open]);

  function validate(): boolean {
    const n = Number.parseInt(seats, 10);
    if (!Number.isFinite(n) || n < 1 || n > 10_000) {
      setSeatsError(t('admin.tenants.detail.modules.activateModal.errorSeats'));
      return false;
    }
    setSeatsError(null);
    return true;
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!validate()) return;
    setIsSubmitting(true);
    setApiError(null);

    try {
      await api.post(`/admin/tenants/${tenantId}/modules/${moduleCode}/activate`, {
        seats: Number.parseInt(seats, 10),
        periodMonths: period,
      });
      onSuccess();
    } catch (err) {
      const axErr = err as AxiosError<{ error: string }>;
      const code = axErr.response?.data?.error;
      let msg: string;
      if (code === 'ALREADY_ACTIVE') {
        msg = t('admin.tenants.detail.modules.activateModal.errorAlreadyActive');
      } else {
        msg = t('admin.tenants.detail.modules.activateModal.errorGeneric');
      }
      setApiError(msg);
      setIsSubmitting(false);
    }
  }

  const footer = (
    <>
      <Button variant="secondary" onClick={onClose} disabled={isSubmitting}>
        {t('admin.tenants.detail.modules.activateModal.cancel')}
      </Button>
      <Button type="submit" form={resolvedFormId} loading={isSubmitting} disabled={isSubmitting}>
        {isSubmitting
          ? t('admin.tenants.detail.modules.activateModal.submitting')
          : t('admin.tenants.detail.modules.activateModal.submit')}
      </Button>
    </>
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('admin.tenants.detail.modules.activateModal.title')}
      description={t('admin.tenants.detail.modules.activateModal.description', {
        module: moduleName,
      })}
      size="sm"
      footer={footer}
      closeOnBackdrop={!isSubmitting}
      closeOnEsc={!isSubmitting}
    >
      <form id={resolvedFormId} onSubmit={handleSubmit} className="flex flex-col gap-4" noValidate>
        <Input
          label={t('admin.tenants.detail.modules.activateModal.seatsLabel')}
          type="number"
          min={1}
          max={10_000}
          value={seats}
          onChange={(e) => setSeats(e.target.value)}
          error={seatsError ?? undefined}
          hint={seatsError ? undefined : t('admin.tenants.detail.modules.activateModal.seatsHint')}
          required
        />
        <FormField label={t('admin.tenants.detail.modules.activateModal.periodLabel')}>
          <div
            role="radiogroup"
            aria-label={t('admin.tenants.detail.modules.activateModal.periodLabel')}
            className="flex flex-col gap-2"
          >
            <label className="inline-flex items-center gap-2 text-sm text-text-primary">
              <input
                type="radio"
                name={`period-${formId}`}
                value="1"
                checked={period === 1}
                onChange={() => setPeriod(1)}
                className="h-4 w-4 accent-accent-600"
              />
              {t('admin.tenants.detail.modules.activateModal.period1m')}
            </label>
            <label className="inline-flex items-center gap-2 text-sm text-text-primary">
              <input
                type="radio"
                name={`period-${formId}`}
                value="12"
                checked={period === 12}
                onChange={() => setPeriod(12)}
                className="h-4 w-4 accent-accent-600"
              />
              {t('admin.tenants.detail.modules.activateModal.period12m')}
            </label>
          </div>
        </FormField>

        {apiError && (
          <div
            role="alert"
            className="rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-300"
          >
            {apiError}
          </div>
        )}
      </form>
    </Modal>
  );
}
